import React, { useState, useEffect, useCallback, useMemo } from 'react';
import trees from './values/trees';
import Controls from './Controls';
import Branch from './Branch';
import Optimizer from './Optimizer';
import Overview from './Overview';
import StatCalculator from './StatCalculator';
import { getMaxSignature } from './utils';
import { DEFAULT_JUICE, MAX_TREE_LEVEL } from './constants';
import './base.scss';
import { version } from '../package.json';


function useForceUpdate() {
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value+1); // update the state to force render
}

const useNumber = (initialValue, min, max) => {
  const [value, setValue] = useState(initialValue);
  const updateValue = useCallback(({ target: { value } }) => {
    if (min !== undefined && value < min) value = min;
    if (max !== undefined && value > max) value = max; 
    setValue(value);
  }, [setValue, min, max]);
  return [value, setValue, updateValue];
};

const useBoolean = (initialValue) => {
  const [value, setValue] = useState(initialValue);
  const updateValue = useCallback(({ target: { checked } }) => {
    setValue(checked);
  }, [setValue]);
  return [value, setValue, updateValue];
};

const view = branch => {
  switch(branch){
    case 'sustenance':
    case 'sorcery':
    case 'might':
    case 'fortitude':
    case 'celerity':
      return Branch;
    case 'statCalculator':
      return StatCalculator;
    case 'overview':
      return Overview;
    case 'optimizer':
      return Optimizer;
    default:
      return null;
  }
}
const saves = JSON.parse(localStorage.saves || "{}");
export default () => {
  const [totalJuice, setTotalJuice] = useState(DEFAULT_JUICE);
  const [juiceLeft, setJuiceLeft] = useState(totalJuice);
  const [celerityLevel, setCelerityLevel] = useState(0);
  const [fortitudeLevel, setFortitudeLevel] = useState(0);
  const [mightLevel, setMightLevel] = useState(0);
  const [sorceryLevel, setSorceryLevel] = useState(0);
  const [sustenanceLevel, setSustenanceLevel] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState('controls');
  const [treeLevel, setTreeLevel] = useState(MAX_TREE_LEVEL);
  const [heroSelection, setHeroSelection] = useState(47);

  const maxSignature = useMemo(() => getMaxSignature(heroSelection), [heroSelection]);

  const [ascension, _setAscension, updateAscension] = useNumber(15, 1, 15);
  const [hasSignature, _setHasSignature, updateHasSignature] = useBoolean(true);
  const [signature, setSignature, updateSignature] = useNumber(Math.min(30, maxSignature), 0, maxSignature);
  const [artifactId, _setArtifactId, updateArtifactId] = useNumber(-1, -1, 7);
  const [artifactLevel, _setArtifactLevel, updateArtifactLevel] = useNumber(0, 0, 5);
  const [unionLevel, _setUnionLevel, updateUnionLevel] = useNumber(3, 0, 3);

  const [weaponLevel, _setWeaponLevel, updateWeaponLevel] = useNumber(0, 0, 5);
  const [helmetLevel, _setHelmetLevel, updateHelmetLevel] = useNumber(0, 0, 5);
  const [chestLevel, _setChestLevel, updateChestLevel] = useNumber(0, 0, 5);
  const [bootsLevel, _setBootsLevel, updateBootsLevel] = useNumber(0, 0, 5);

  const [weaponQuality, _setWeaponQuality, updateWeaponQuality] = useNumber(9, 0, 13);
  const [helmetQuality, _setHelmetQuality, updateHelmetQuality] = useNumber(9, 0, 13);
  const [chestQuality, _setChestQuality, updateChestQuality] = useNumber(9, 0, 13);
  const [bootsQuality, _setBootsQuality, updateBootsQuality] = useNumber(9, 0, 13);

  const [weaponFaction, _setWeaponFaction, updateWeaponFaction] = useBoolean(false);
  const [helmetFaction, _setHelmetFaction, updateHelmetFaction] = useBoolean(false);
  const [chestFaction, _setChestFaction, updateChestFaction] = useBoolean(false);
  const [bootsFaction, _setBootsFaction, updateBootsFaction] = useBoolean(false);

  const [crystal, setCrystal] = useState(240);


  const updateHeroSelection = useCallback(({ target: { value } }) => {
    setHeroSelection(value);
    const maxSignature = getMaxSignature(value);
    if(signature > maxSignature) setSignature(maxSignature);
  }, [signature, setSignature, setHeroSelection]);

  const updateCrystal = useCallback(({ target: { value } }) => {
    if(value <= 240) value = Math.floor(value);
    if(value > 604.9) value = 604.9;
    if(value < 0) value = 0;
    setCrystal(value);
  }, [setCrystal]);

  const forceUpdate = useForceUpdate();
  const [loaded, setLoaded] = useState(false);
  const save = (name) => {
    if(name) {
      saves[name] = {
        totalJuice,
        juiceLeft,
        celerityLevel,
        fortitudeLevel,
        mightLevel,
        sorceryLevel,
        sustenanceLevel,
        treeLevel
      }
      localStorage.setItem('saves', JSON.stringify(saves));
      forceUpdate();
      window.location.hash = shareCode();
    }
  }
  const shareCode = () => {
    const saveData = btoa(JSON.stringify({
      j: totalJuice,
      l: juiceLeft,
      c: celerityLevel,
      f: fortitudeLevel,
      m: mightLevel,
      s: sorceryLevel,
      u: sustenanceLevel,
      t: treeLevel
    }));
    return saveData;
  };
  const loadCode = code => {
    try {
      const saveData = JSON.parse(atob(code));
      const {
        j: totalJuice,
        l: juiceLeft,
        c: celerityLevel,
        f: fortitudeLevel,
        m: mightLevel,
        s: sorceryLevel,
        u: sustenanceLevel,
        t: treeLevel
      } = saveData;
      setTotalJuice(totalJuice);
      setJuiceLeft(juiceLeft);
      setCelerityLevel(celerityLevel);
      setFortitudeLevel(fortitudeLevel);
      setMightLevel(mightLevel);
      setSorceryLevel(sorceryLevel);
      setSustenanceLevel(sustenanceLevel);
      setTreeLevel(treeLevel);
    } catch (e) {
      console.error("Invalid code");
    }
  }
  const load = (name) => {
    const save = saves[name];
    if(save){
      setTotalJuice(save.totalJuice);
      setJuiceLeft(save.juiceLeft);
      setCelerityLevel(save.celerityLevel);
      setFortitudeLevel(save.fortitudeLevel);
      setMightLevel(save.mightLevel);
      setSorceryLevel(save.sorceryLevel);
      setSustenanceLevel(save.sustenanceLevel);
      setTreeLevel(save.treeLevel);
    }
  }
  const removeSave = (name) => {
    if(saves[name]){
      delete saves[name];
      localStorage.setItem('saves', JSON.stringify(saves));
      forceUpdate();
    }
  }

  const resetAll = (newJuice) => {
    setTotalJuice(parseInt(newJuice));
    setJuiceLeft(parseInt(newJuice));
    setCelerityLevel(0);
    setFortitudeLevel(0);
    setMightLevel(0);
    setSorceryLevel(0);
    setSustenanceLevel(0);
  };

  const statProps = {
    ascension,
    updateAscension,
    hasSignature,
    updateHasSignature,
    signature,
    setSignature,
    updateSignature,
    artifactId,
    updateArtifactId,
    artifactLevel,
    updateArtifactLevel,
    unionLevel,
    updateUnionLevel,
    
    weaponLevel,
    updateWeaponLevel,
    helmetLevel,
    updateHelmetLevel,
    chestLevel,
    updateChestLevel,
    bootsLevel,
    updateBootsLevel,
    
    weaponQuality,
    updateWeaponQuality,
    helmetQuality,
    updateHelmetQuality,
    chestQuality,
    updateChestQuality,
    bootsQuality,
    updateBootsQuality,
    
    weaponFaction,
    updateWeaponFaction,
    helmetFaction,
    updateHelmetFaction,
    chestFaction,
    updateChestFaction,
    bootsFaction,
    updateBootsFaction,
    
    crystal,
    setCrystal,
    updateCrystal,
    heroSelection,
    updateHeroSelection,
    maxSignature,
  };

  const controlProps = {
    totalJuice,
    setTotalJuice,
    selectedBranch,
    setSelectedBranch,
    juiceLeft,
    setJuiceLeft,
    resetAll,
    sustenanceLevel,
    sorceryLevel,
    mightLevel,
    fortitudeLevel,
    celerityLevel,
    treeLevel,
    setTreeLevel,
    save,
    load,
    removeSave,
    saveNames: Object.keys(saves)
  };
  const branchProps = {
    branch: selectedBranch,
    stats: trees[selectedBranch],
    juiceLeft,
    setJuiceLeft,
    treeLevel,
  };
  useEffect(() => {
    if(!loaded && window.location.hash){
      loadCode(window.location.hash.substring(1));
    }
    if(!loaded) setLoaded(true);
  }, [loaded]);

  switch (selectedBranch) {
    case 'celerity':
      branchProps.level = celerityLevel;
      branchProps.setLevel = setCelerityLevel;
      break;
    case 'fortitude':
      branchProps.level = fortitudeLevel;
      branchProps.setLevel = setFortitudeLevel;
      break;
    case 'might':
      branchProps.level = mightLevel;
      branchProps.setLevel = setMightLevel;
      break;
    case 'sorcery':
      branchProps.level = sorceryLevel;
      branchProps.setLevel = setSorceryLevel;
      break;
    case 'sustenance':
      branchProps.level = sustenanceLevel;
      branchProps.setLevel = setSustenanceLevel;
      break;
    default:
      break;
  };

  const View = view(selectedBranch);

  const allProps = {
    totalJuice, setTotalJuice,
    juiceLeft, setJuiceLeft,
    celerityLevel, setCelerityLevel,
    fortitudeLevel, setFortitudeLevel,
    mightLevel, setMightLevel,
    sorceryLevel, setSorceryLevel,
    sustenanceLevel, setSustenanceLevel,
    selectedBranch, setSelectedBranch,
    treeLevel, setTreeLevel,
    ...controlProps,
    ...branchProps,
    ...statProps,
  }

  return (
    <div id="tree-juicer">
      <Controls {...controlProps} />
      {View && <View {...allProps} />}
      <div id="footer">
        <span>♥ Created by ☆Skye☆ ♥</span>
        <span>Shoutouts to Fishstix, Hashimaru, Mei, Banana, and Eirenes</span>
        <span>v{version}</span>
      </div>
    </div>
  );

}